import { useCallback, useEffect, useRef, useState } from "react";
import { DemoNote } from "@/components/DemoNote";
import { FixChecklist } from "@/components/FixChecklist";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Loader2, Pencil, RotateCcw } from "lucide-react";
import { ACCEPT, api, errMsg, MSG, pendingUploads, validateFile } from "@/lib/api";
import { usePoll } from "@/lib/hooks";
import { SectionHead, SiteFooter } from "@/components/Chrome";
import { PhotoCard, RoomAnalysis, UploadTile } from "@/components/workspace/Gallery";
import { BriefForm, DesignSpecCard, Directions } from "@/components/workspace/Design";
import { ImportOutcome } from "@/components/workspace/ImportOutcome";
import { ListingFacts, ListingRetrieval } from "@/components/workspace/ListingRetrieval";
import { PermissionCheck } from "@/components/Provenance";

let seq = 0;

const RoomTitle = ({ room, onRename }) => {
  const [edit, setEdit] = useState(null);
  if (edit !== null) {
    return (
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (edit.trim()) onRename(room, edit.trim()); setEdit(null); }}>
        <input autoFocus className="field !min-h-[44px] max-w-xs" value={edit} onChange={(e) => setEdit(e.target.value)} data-testid={`room-rename-input-${room.id}`} />
        <button className="btn-primary !min-h-[44px] !px-4 text-sm" data-testid={`room-rename-save-${room.id}`}>Save</button>
      </form>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <h3 className="font-display text-3xl tracking-tight" data-testid={`room-name-${room.id}`}>{room.name}</h3>
      <button className="btn-quiet" onClick={() => setEdit(room.name)} aria-label="Rename room" data-testid={`room-rename-button-${room.id}`}><Pencil size={14} /></button>
    </div>
  );
};

export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [prop, setProp] = useState(null);
  const [fullRemodel, setFullRemodel] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [uploads, setUploads] = useState([]);
  const [savingSpec, setSavingSpec] = useState(null);
  const [generatingPhoto, setGeneratingPhoto] = useState(null);
  const [perm, setPerm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${id}`);
      setProp(data);
      return data;
    } catch (e) {
      setLoadError(e?.response?.status === 403 ? "This workspace belongs to another guest." : "We couldn't open this workspace.");
      return null;
    }
  }, [id]);

  const patchUpload = (key, fields) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...fields } : x)));

  const uploadOne = async (item) => {
    patchUpload(item.key, { status: "uploading", progress: 0, error: "" });
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("permission", "true");
    try {
      await api.post(`/properties/${id}/photos`, fd, { onUploadProgress: (e) => patchUpload(item.key, { progress: Math.round((e.loaded / (e.total || 1)) * 100) }) });
      patchUpload(item.key, { status: "done", progress: 100 });
      return true;
    } catch (e) {
      const st = e?.response?.status;
      const known = [409, 413, 415].includes(st);
      patchUpload(item.key, { status: known ? "rejected" : "error", error: known ? errMsg(e, MSG.type) : "Upload didn't finish." });
      return false;
    }
  };

  const afterBatch = async (anyOk) => {
    const data = await load();
    setUploads((u) => u.filter((x) => x.status !== "done"));
    if (anyOk && data) {
      await api.post(`/properties/${id}/analyze`).catch(() => {});
      load();
    }
  };

  const startUploads = async (files, current) => {
    let slots = 20 - (current?.photos.length || 0) - uploads.filter((u) => ["queued", "uploading"].includes(u.status)).length;
    const items = files.map((f) => {
      const key = `u${++seq}`;
      const err = validateFile(f);
      const preview = err ? null : URL.createObjectURL(f);
      if (err) return { key, file: f, status: "rejected", error: err };
      if (slots <= 0) return { key, file: f, preview, status: "rejected", error: MSG.count };
      slots -= 1;
      return { key, file: f, preview, status: "queued", progress: 0 };
    });
    setUploads((u) => [...u, ...items]);
    const queue = items.filter((i) => i.status === "queued");
    let ok = false;
    const worker = async () => { while (queue.length) { const it = queue.shift(); ok = (await uploadOne(it)) || ok; } };
    await Promise.all([worker(), worker(), worker()]);
    if (queue.length === 0 && items.some((i) => i.status === "queued")) await afterBatch(ok);
  };

  useEffect(() => {
    load().then((data) => {
      const files = pendingUploads.get(id);
      if (data && files?.length) {
        pendingUploads.delete(id);
        startUploads(files, data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const analysing = prop?.analysisStatus === "running";
  usePoll(load, analysing || prop?.visions?.some((v) => v.status === "queued" || v.status === "running"), 3000);

  const onRetry = async (item) => { const ok = await uploadOne(item); await afterBatch(ok); };
  const onMove = async (photo, roomId) => { await api.patch(`/photos/${photo.id}`, { roomId }); load(); };
  const onCreateRoom = async (photo, name) => {
    const { data } = await api.post(`/properties/${id}/rooms`, { name });
    await onMove(photo, data.id);
  };
  const onRename = async (room, name) => { await api.patch(`/rooms/${room.id}`, { name }); load(); };
  const onGenerate = async (photo) => {
    setGeneratingPhoto(photo.id);
    try {
      const { data } = await api.post(`/photos/${photo.id}/visions`, { fullRemodel });
      nav(`/v/${data.id}`);
    } catch (e) {
      toast(errMsg(e, "This vision didn't render."));
      setGeneratingPhoto(null);
    }
  };
  const saveSpec = async (body, key) => {
    setSavingSpec(key);
    try { await api.put(`/properties/${id}/design-spec`, body); await load(); toast("Design specification saved"); }
    catch (e) { toast(errMsg(e, "Couldn't save. Try again.")); }
    setSavingSpec(null);
  };
  const retryAnalysis = async () => { await api.post(`/properties/${id}/analyze`).catch(() => {}); load(); };

  if (loadError) return <main className="mx-auto max-w-3xl px-5 py-24"><p className="font-display text-3xl" data-testid="workspace-error">{loadError}</p><Link to="/" className="btn-primary mt-6">Start a Project</Link></main>;
  if (!prop) return <main className="mx-auto max-w-7xl px-5 py-16"><div className="h-72 rounded bg-[var(--bone-2)] shimmer" /></main>;

  const roomsWithPhotos = prop.rooms.map((r) => ({ ...r, photos: prop.photos.filter((p) => p.roomId === r.id) })).filter((r) => r.photos.length || r.renamed);
  const notProperty = prop.photos.filter((p) => p.isPropertyPhoto === false && !p.roomId);
  const unassigned = prop.photos.filter((p) => !p.roomId && p.isPropertyPhoto !== false);
  const visionFor = (pid) => prop.visions.find((v) => v.photoId === pid);
  const inFlight = uploads.some((u) => ["queued", "uploading"].includes(u.status));
  const cardProps = { rooms: prop.rooms, onMove, onCreateRoom, onGenerate };

  return (
    <main data-testid="workspace-page">
      <div className="mx-auto max-w-7xl px-5 pt-10 sm:px-8 lg:px-12">
        <div className="eyebrow mb-3">Property workspace</div>
        <h1 className="font-display text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="property-title">{prop.title}</h1>
        <DemoNote show={prop.demoScenario} />
        {prop.listingUrl && (
          <a href={prop.listingUrl} target="_blank" rel="noreferrer noopener" className="btn-quiet mt-2 max-w-full truncate" data-testid="property-listing-link">
            Listing link: {prop.listingUrl}
          </a>
        )}
        <ListingFacts prop={prop} />
        {prop.summary && (
          <div className="mt-8 grid gap-6 border-y border-[var(--line)] py-6 sm:grid-cols-[1fr_2fr]" data-testid="property-summary">
            <div>
              <div className="eyebrow mb-2">Style &amp; era</div>
              <div className="font-display text-2xl leading-tight" data-testid="property-style">{prop.summary.architecturalStyle}</div>
              <div className="mt-1 text-sm text-[var(--stone-dark)]" data-testid="property-era">{prop.summary.era}</div>
            </div>
            <p className="text-sm leading-relaxed text-[var(--ink-soft)] sm:text-base" data-testid="property-summary-text">{prop.summary.summary}</p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {prop.photos.length > 0 && <PermissionCheck checked={perm} onChange={setPerm} testid="workspace-permission-checkbox" />}
          <button className="btn-ghost" onClick={() => fileRef.current?.click()} disabled={prop.photos.length >= 20 || (prop.photos.length > 0 && !perm)} data-testid="workspace-add-photos-button">
            <ImagePlus size={16} /> {prop.photos.length ? "Add photos" : "Upload property photographs"}
          </button>
          <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="workspace-photo-input" onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) startUploads(f, prop); }} />
          <span className="text-xs text-[var(--stone-dark)]" data-testid="photo-count">{prop.photos.length} / 20 photos</span>
          {prop.photos.length > 0 && (
            <label className="flex items-center gap-2 text-xs" title="Off keeps walls, windows, cabinets and appliances where they are" data-testid="full-remodel-toggle">
              <input type="checkbox" checked={fullRemodel} onChange={(e) => setFullRemodel(e.target.checked)} data-testid="full-remodel-checkbox" />
              Full remodel <span className="text-[var(--stone-dark)]">(lets Generate change the layout)</span>
            </label>
          )}
        </div>
        {prop.photos.length >= 20 && <p className="mt-2 text-sm text-[#B42318]" data-testid="photo-limit-message">{MSG.count}</p>}

        {prop.photos.length === 0 && !inFlight && (() => {
          const onAdded = async () => { await load(); await api.post(`/properties/${id}/analyze`).catch(() => {}); load(); };
          const outcome = <ImportOutcome prop={prop} onUpload={() => fileRef.current?.click()} onAdded={onAdded} />;
          return prop.listingRetrieval ? <ListingRetrieval prop={prop} reload={load} onImported={onAdded} fallback={outcome} /> : outcome;
        })()}

        {uploads.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="upload-tiles">
            {uploads.map((u) => <UploadTile key={u.key} item={u} onRetry={onRetry} onDismiss={(it) => setUploads((x) => x.filter((y) => y.key !== it.key))} />)}
          </div>
        )}

        {analysing && (
          <div className="mt-8 flex items-center gap-3 rounded-xl bg-[var(--charcoal)] px-5 py-4 text-[var(--bone)]" data-testid="analysis-running">
            <Loader2 size={18} className="animate-spin" /> <span className="font-display text-xl">Analyzing your property…</span>
          </div>
        )}
        {prop.analysisStatus === "failed" && (
          <div className="mt-8 flex flex-wrap items-center gap-4 rounded-xl border border-[#B42318]/30 bg-[#FDECEA] px-5 py-4" data-testid="analysis-failed">
            <span className="font-display text-xl">Analysis didn't finish.</span>
            <button className="btn-primary" onClick={retryAnalysis} data-testid="analysis-retry-button"><RotateCcw size={15} /> Try again</button>
          </div>
        )}

        {unassigned.length > 0 && (
          <section className="mt-12" data-testid="unassigned-photos">
            <div className="eyebrow mb-4">{analysing ? "Awaiting room analysis" : "Photos"}</div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {unassigned.map((p) => <PhotoCard key={p.id} photo={p} vision={visionFor(p.id)} generating={generatingPhoto === p.id} {...cardProps} />)}
            </div>
          </section>
        )}

        {roomsWithPhotos.map((r) => (
          <section key={r.id} className="mt-14 border-t border-[var(--charcoal)]/15 pt-8" data-testid={`room-section-${r.id}`}>
            <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
              <RoomTitle room={r} onRename={onRename} />
              <span className="eyebrow" data-testid={`room-type-${r.id}`}>{r.roomType}</span>
            </div>
            {r.photos.some((p) => p.isPropertyPhoto !== false) && <div className="mb-5"><FixChecklist roomId={r.id} /></div>}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {r.photos.map((p) => <PhotoCard key={p.id} photo={p} vision={visionFor(p.id)} generating={generatingPhoto === p.id} {...cardProps} />)}
            </div>
            <RoomAnalysis room={r} />
          </section>
        ))}

        {notProperty.length > 0 && (
          <section className="mt-14 border-t border-[var(--charcoal)]/15 pt-8" data-testid="not-property-section">
            <h3 className="mb-5 font-display text-3xl tracking-tight">Not a property photo</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {notProperty.map((p) => <PhotoCard key={p.id} photo={p} {...cardProps} />)}
            </div>
          </section>
        )}

        {prop.photos.length > 0 && (
          <section className="mt-20" data-testid="design-section">
            <SectionHead eyebrow="Design" title="Choose a vision">One property-wide plan — repair, restore, preserve, modernize sensitively — is applied to every room.</SectionHead>
            {prop.directions?.length === 3 && <Directions directions={prop.directions} selectedId={prop.designSpec?.directionId} busyId={typeof savingSpec === "string" && savingSpec !== "brief" ? savingSpec : null} onSelect={(did) => saveSpec({ directionId: did }, did)} />}
            {prop.analysisStatus === "done" && prop.directions?.length !== 3 && <p className="text-sm text-[var(--ink-soft)]" data-testid="directions-unavailable">Suggested directions aren't available right now — describe what you want in the brief below and continue.</p>}
            {analysing && !prop.directions?.length && <div className="grid gap-4 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-64 rounded bg-[var(--bone-2)] shimmer" />)}</div>}
            <div className="mt-12 grid gap-12 lg:grid-cols-2">
              <div>
                <div className="eyebrow mb-4">Brief</div>
                <BriefForm spec={prop.designSpec} saving={savingSpec === "brief"} onSave={(brief) => saveSpec({ brief }, "brief")} />
              </div>
              <div className="rounded-xl bg-[var(--bone-2)] p-5 sm:p-7">
                <div className="eyebrow mb-4">Design specification · applied to every room</div>
                <DesignSpecCard spec={prop.designSpec} />
              </div>
            </div>
          </section>
        )}
      </div>
      <SiteFooter />
    </main>
  );
}
